import * as React from 'react';
import { ActionButton, DefaultButton, Link, MessageBar, MessageBarType, Spinner, SpinnerSize } from '@fluentui/react';
import styles from './SharePointOrganizer.module.scss';
import type { IAuditLog } from '../../../engine/AuditLog';
import type { IDriveWriter } from '../../../engine/DriveWriter';
import type { ILibraryMetadata } from '../../../tagging/Tagging';
import { type IBatchSummary, summarizeBatches, UndoEngine } from '../../../engine/UndoEngine';
import type { ILibraryInfo } from '../../../models/LibraryInfo';
import type { IOperationLogEntry } from '../../../models/Operation';
import { describeGraphError } from '../../../utils/errors';
import { formatNumber } from '../../../utils/format';
import type { ITelemetry } from '../../../utils/telemetry';
import UndoPanel from './UndoPanel';

export interface IHistoryViewProps {
  library: ILibraryInfo;
  auditLog: IAuditLog;
  createWriter: () => IDriveWriter;
  /** Lets Undo clear tags; absent in the web part. */
  createMetadata?: () => ILibraryMetadata;
  userName: string;
  allowChanges: boolean;
  telemetry: ITelemetry;
  onRescan: () => void;
}

const ACTION_LABEL: { [action: string]: string } = {
  move: 'Moved',
  rename: 'Renamed',
  quarantine: 'Quarantined',
  createFolder: 'Created folder',
  undo: 'Undone',
  tag: 'Tagged',
  createColumn: 'Created column'
};

function describeTags(json: string): string {
  try {
    const values: { [column: string]: unknown } = JSON.parse(json || '{}');
    return Object.keys(values).map(k => `${k} = ${values[k]}`).join(', ');
  } catch {
    return '';
  }
}

const STATE_LABEL: { [state: string]: string } = {
  applied: 'Applied',
  undone: 'Undone',
  partiallyUndone: 'Partly undone'
};

function when(timestamp: string): string {
  const date: Date = new Date(timestamp);
  return isNaN(date.getTime()) ? timestamp : date.toLocaleString();
}

function OperationRow(props: { entry: IOperationLogEntry; undone: boolean }): React.ReactElement {
  const { entry, undone } = props;
  return (
    <li className={styles.historyOp}>
      <span className={styles.historyOpAction}>{ACTION_LABEL[entry.action] ?? entry.action}</span>
      <span className={styles.historyOpPath}>
        {entry.action === 'createFolder' ? entry.destinationPath
          : entry.action === 'createColumn' ? `${entry.newName} (in ${entry.destinationPath})`
            : entry.action === 'tag' ? `${entry.newName}: ${describeTags(entry.destinationPath)}`
              : `${entry.sourcePath} → ${entry.destinationPath}`}
      </span>
      <span className={styles.treeMeta}>
        {undone ? 'undone' : entry.status}{entry.status === 'failed' && entry.errorMessage ? ` — ${entry.errorMessage}` : ''}
      </span>
    </li>
  );
}

/** Batches applied to this library, newest first, with one-click undo for the newest. */
export default function HistoryView(props: IHistoryViewProps): React.ReactElement {
  const { library, auditLog, createWriter, createMetadata, userName, allowChanges, telemetry, onRescan } = props;
  const [batches, setBatches] = React.useState<IBatchSummary[] | undefined>(undefined);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [expanded, setExpanded] = React.useState<{ [batchId: string]: boolean }>({});
  const [undoing, setUndoing] = React.useState<{ batch: IBatchSummary; engine: UndoEngine } | undefined>(undefined);
  const [reloadKey, setReloadKey] = React.useState<number>(0);

  React.useEffect(() => {
    let active: boolean = true;
    setBatches(undefined);
    setError(undefined);
    auditLog.readEntries(library.id)
      .then(entries => { if (active) { setBatches(summarizeBatches(entries)); } })
      .catch(e => {
        telemetry.error('history.failed', e, { libraryId: library.id });
        if (active) { setError(describeGraphError(e)); }
      });
    return () => { active = false; };
  }, [auditLog, library, reloadKey, telemetry]);

  const startUndo = (batch: IBatchSummary): void => {
    setUndoing({
      batch,
      engine: new UndoEngine({
        writer: createWriter(), metadata: createMetadata ? createMetadata() : undefined,
        audit: auditLog, libraryId: library.id, libraryName: library.name, user: userName
      })
    });
  };

  if (error) {
    return <MessageBar messageBarType={MessageBarType.error} isMultiline>{error}</MessageBar>;
  }
  if (!batches) {
    return <Spinner size={SpinnerSize.medium} label="Loading history…" />;
  }

  return (
    <div className={styles.plan}>
      <div className={styles.compareHeader}>
        <h3 className={styles.sectionTitle}>History</h3>
        <ActionButton iconProps={{ iconName: 'Refresh' }} text="Refresh" onClick={() => setReloadKey(k => k + 1)} />
      </div>
      <p className={styles.issueListDescription}>
        Every batch of changes applied to {library.name}, newest first. Batches are undone newest first, because a later
        batch may have moved the same items. Undo puts every item back in its original folder with its original name,
        and removes the empty folders SharePoint Organizer created. Details are in the{' '}
        <Link href={auditLog.viewUrl} target="_blank" rel="noreferrer">audit log</Link>.
      </p>
      {!allowChanges && (
        <MessageBar isMultiline>
          <strong>Recommend-only mode.</strong> To undo a batch, edit this web part and turn on <strong>Allow changes to this site</strong>.
        </MessageBar>
      )}

      {batches.length === 0 && <MessageBar>No changes have been applied to this library yet.</MessageBar>}

      <ul className={styles.changes}>
        {batches.map(batch => {
          const statusClass: string = batch.state === 'undone' ? styles.changeRejected : batch.state === 'partiallyUndone' ? '' : styles.changeApproved;
          const olderBlocked: boolean = !batch.canUndo && batch.state !== 'undone' && batch.reversible.length > 0;
          return (
            <li key={batch.batchId} className={`${styles.change} ${statusClass}`}>
              <div className={styles.changeHeader}>
                <span className={styles.changeType}>{when(batch.startedAt)}</span>
                <span className={`${styles.bucket} ${batch.state === 'applied' ? styles.bucketHigh : styles.bucketMedium}`}>
                  {STATE_LABEL[batch.state]}
                </span>
              </div>
              <div className={styles.changeTitle}>
                {formatNumber(batch.succeeded)} {batch.succeeded === 1 ? 'change' : 'changes'} applied
                {batch.skipped > 0 && ` · ${formatNumber(batch.skipped)} already done`}
                {batch.failed > 0 && ` · ${formatNumber(batch.failed)} failed`}
              </div>
              <p className={styles.changeReason}>By {batch.user} · <code>{batch.batchId}</code></p>

              <div className={styles.changeActions}>
                {batch.canUndo && (
                  <DefaultButton
                    text={batch.state === 'partiallyUndone' ? 'Undo the rest' : 'Undo this batch'}
                    iconProps={{ iconName: 'Undo' }}
                    disabled={!allowChanges}
                    onClick={() => startUndo(batch)}
                  />
                )}
                <ActionButton
                  iconProps={{ iconName: expanded[batch.batchId] ? 'ChevronUp' : 'ChevronDown' }}
                  text={expanded[batch.batchId] ? 'Hide details' : 'Show details'}
                  onClick={() => setExpanded(prev => ({ ...prev, [batch.batchId]: !prev[batch.batchId] }))}
                />
              </div>
              {olderBlocked && <p className={styles.applyProblem}>Undo the newer batches first to undo this one.</p>}

              {expanded[batch.batchId] && (
                <ul className={styles.historyOps}>
                  {batch.operations.map(e => <OperationRow key={e.operationId} entry={e} undone={!!batch.undone[e.operationId]} />)}
                </ul>
              )}
            </li>
          );
        })}
      </ul>

      {undoing && (
        <UndoPanel
          batch={undoing.batch}
          engine={undoing.engine}
          auditLogUrl={auditLog.viewUrl}
          telemetry={telemetry}
          libraryId={library.id}
          onDismiss={() => { setUndoing(undefined); setReloadKey(k => k + 1); }}
          onRescan={() => { setUndoing(undefined); onRescan(); }}
        />
      )}
    </div>
  );
}
